import React, { useState } from "react";
import { useFormik } from "formik";
import {
  Calendar,
  Clock,
  MapPin,
  Building2,
  Globe,
  Navigation,
  AlertCircle,
  CheckCircle2,
  FileText,
} from "lucide-react";
import { toast } from "react-toastify";

const EVENT_TYPES = [
  { id: "concerts", label: "Concerts", icon: "🎵" },
  { id: "drama", label: "Drama", icon: "🎭" },
  { id: "festival", label: "Festival", icon: "🎪" },
  { id: "sports", label: "Sports", icon: "⚽" },
  { id: "theatre", label: "Theatre", icon: "🎬" },
  { id: "performing_arts", label: "Performing Arts", icon: "🎨" },
  { id: "career_fairs", label: "Career Fairs", icon: "💼" },
  { id: "meetups", label: "Meetups", icon: "🤝" },
];

/**
 * Validation Schema
 */
const validate = (values) => {
  const errors = {};

  // Event Title validation
  if (!values.eventName || values.eventName.trim() === "") {
    errors.eventName = "Event title is required";
  } else if (values.eventName.trim().length < 3) {
    errors.eventName = "Event title must be at least 3 characters";
  }

  // Event Type validation
  if (!values.eventType || values.eventType.length === 0) {
    errors.eventType = "Please select at least one event type";
  } else if (values.eventType.length > 2) {
    errors.eventType = "You can select a maximum of 2 event types";
  }

  // Start Date validation
  if (!values.eventStartDate) {
    errors.eventStartDate = "Start date is required";
  } else {
    const selectedDate = new Date(values.eventStartDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      errors.eventStartDate = "Start date cannot be in the past";
    }
  }

  // End Date validation
  if (!values.eventEndDate) {
    errors.eventEndDate = "End date is required";
  } else {
    const endDate = new Date(values.eventEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (endDate < today) {
      errors.eventEndDate = "End date cannot be in the past";
    }
    if (values.eventStartDate && values.eventEndDate && values.eventEndDate < values.eventStartDate) {
      errors.eventEndDate = "End date must be on or after start date";
    }
  }

  // Start Time validation
  if (!values.eventStartTime) {
    errors.eventStartTime = "Start time is required";
  }

  // End Time validation
  if (!values.eventEndTime) {
    errors.eventEndTime = "End time is required";
  }

  // Venue Name validation
  if (!values.venueName) {
    errors.venueName = "Venue name is required";
  } else if (values.venueName.length < 3) {
    errors.venueName = "Venue name must be at least 3 characters";
  }

  // Venue Street Address validation
  if (!values.venueStreetAddress) {
    errors.venueStreetAddress = "Venue street address is required";
  } else if (values.venueStreetAddress.length < 5) {
    errors.venueStreetAddress = "Please provide a complete address";
  }

  // Country validation
  if (!values.country) {
    errors.country = "Country is required";
  }

  // City validation
  if (!values.city) {
    errors.city = "City/State/Region is required";
  } else if (values.city.length < 2) {
    errors.city = "Please provide a valid city/state/region";
  }

  if (values.venueGoogleLocation && values.venueGoogleLocation.trim()) {
    const urlPattern = /^(https?:\/\/)?(www\.)?(maps\.google\.|goo\.gl\/maps|maps\.app\.goo\.gl)/i;
    if (!urlPattern.test(values.venueGoogleLocation)) {
      errors.venueGoogleLocation = "Please provide a valid Google Maps URL";
    }
  }

  return errors;
};

/**
 * Event and Venue Information Section Component
 * Handles event type, date, time, and venue details
 */
const EventVenueInfoSection = ({ data = {}, onUpdate }) => {
  const [selectedEventTypes, setSelectedEventTypes] = useState(
    data.eventType || []
  );

  const formik = useFormik({
    initialValues: {
      eventName: data.eventName || "",
      eventType: data.eventType || [],
      eventStartDate: data.eventStartDate || data.eventDate || "",
      eventEndDate: data.eventEndDate || data.eventDate || "",
      eventStartTime: data.eventStartTime || data.eventTime || "",
      eventEndTime: data.eventEndTime || data.eventTime || "",
      venueName: data.venueName || "",
      venueStreetAddress: data.venueStreetAddress || "",
      country: data.country || "",
      city: data.city || "",
      venueGoogleLocation: data.venueGoogleLocation || "",
    },
    validate,
    enableReinitialize: true,
    onSubmit: (values) => {
      // This will be handled by parent component
    },
  });

  // Handle event type selection
  const handleEventTypeToggle = (eventTypeId) => {
    const currentTypes = formik.values.eventType || [];
    const isSelected = currentTypes.includes(eventTypeId);

    let newTypes;
    if (isSelected) {
      // Remove if already selected
      newTypes = currentTypes.filter((id) => id !== eventTypeId);
    } else {
      // Add if not selected, but max 2
      if (currentTypes.length >= 2) {
        toast.warning("You can select a maximum of 2 event types");
        return;
      }
      newTypes = [...currentTypes, eventTypeId];
    }

    setSelectedEventTypes(newTypes);
    formik.setFieldValue("eventType", newTypes);
    handleFieldChange("eventType", newTypes);
  };

  // Handle field changes and notify parent
  const handleFieldChange = (field, value) => {
    formik.setFieldValue(field, value);
    formik.setFieldTouched(field, true);
    onUpdate({
      [field]: value,
    });
  };

  return (
    <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
      <h3 className="flex items-center mb-6 text-lg font-bold text-gray-900">
        <Building2 className="mr-2 w-5 h-5 text-purple-600" />
        Event & Venue Information
      </h3>

      <div className="space-y-6">
        {/* Event Type Selection */}
        <div>
          <label className="block mb-3 text-sm font-semibold text-gray-700">
            Event Type <span className="text-red-500">*</span>
            <span className="ml-2 text-xs font-normal text-gray-500">
              (Select 1-2 types)
            </span>
          </label>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {EVENT_TYPES.map((type) => {
              const isSelected = formik.values.eventType?.includes(type.id);
              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleEventTypeToggle(type.id)}
                  className={`relative p-4 text-left border-2 rounded-xl transition-all duration-200 ${
                    isSelected
                      ? "border-purple-500 bg-purple-50 shadow-md scale-[1.02]"
                      : "border-gray-200 bg-white hover:border-purple-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{type.icon}</span>
                    <span
                      className={`text-sm font-semibold ${
                        isSelected ? "text-purple-900" : "text-gray-700"
                      }`}
                    >
                      {type.label}
                    </span>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle2 className="w-5 h-5 text-purple-600" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {formik.touched.eventType && formik.errors.eventType && (
            <div className="flex items-center mt-2 text-xs text-red-600">
              <AlertCircle className="mr-1 w-3 h-3" />
              {formik.errors.eventType}
            </div>
          )}
        </div>

        {/* Event Title */}
        <div>
          <label className="block mb-1.5 text-xs font-semibold text-gray-700">
            Event Title <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <FileText className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
            <input
              type="text"
              name="eventName"
              placeholder="e.g., Summer Music Festival 2024"
              value={formik.values.eventName}
              onChange={(e) => handleFieldChange("eventName", e.target.value)}
              onBlur={formik.handleBlur}
              className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                formik.touched.eventName && formik.errors.eventName
                  ? "border-red-300 bg-red-50"
                  : "border-gray-200 bg-gray-50 focus:bg-white"
              }`}
            />
          </div>
          {formik.touched.eventName && formik.errors.eventName && (
            <div className="flex items-center mt-1 text-xs text-red-600">
              <AlertCircle className="mr-1 w-3 h-3" />
              {formik.errors.eventName}
            </div>
          )}
        </div>

        {/* Event Start Date, End Date and Time */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Start Date */}
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-gray-700">
              Start Date <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
              <input
                type="date"
                name="eventStartDate"
                value={formik.values.eventStartDate}
                onChange={(e) => handleFieldChange("eventStartDate", e.target.value)}
                onBlur={formik.handleBlur}
                min={new Date().toISOString().split("T")[0]}
                className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                  formik.touched.eventStartDate && formik.errors.eventStartDate
                    ? "border-red-300 bg-red-50"
                    : "border-gray-200 bg-gray-50 focus:bg-white"
                }`}
              />
            </div>
            {formik.touched.eventStartDate && formik.errors.eventStartDate && (
              <div className="flex items-center mt-1 text-xs text-red-600">
                <AlertCircle className="mr-1 w-3 h-3" />
                {formik.errors.eventStartDate}
              </div>
            )}
          </div>

          {/* End Date */}
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-gray-700">
              End Date <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
              <input
                type="date"
                name="eventEndDate"
                value={formik.values.eventEndDate}
                onChange={(e) => handleFieldChange("eventEndDate", e.target.value)}
                onBlur={formik.handleBlur}
                min={formik.values.eventStartDate || new Date().toISOString().split("T")[0]}
                className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                  formik.touched.eventEndDate && formik.errors.eventEndDate
                    ? "border-red-300 bg-red-50"
                    : "border-gray-200 bg-gray-50 focus:bg-white"
                }`}
              />
            </div>
            {formik.touched.eventEndDate && formik.errors.eventEndDate && (
              <div className="flex items-center mt-1 text-xs text-red-600">
                <AlertCircle className="mr-1 w-3 h-3" />
                {formik.errors.eventEndDate}
              </div>
            )}
          </div>

          {/* Start Time */}
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-gray-700">
              Start Time <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
              <input
                type="time"
                name="eventStartTime"
                value={formik.values.eventStartTime}
                onChange={(e) => handleFieldChange("eventStartTime", e.target.value)}
                onBlur={formik.handleBlur}
                className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                  formik.touched.eventStartTime && formik.errors.eventStartTime
                    ? "border-red-300 bg-red-50"
                    : "border-gray-200 bg-gray-50 focus:bg-white"
                }`}
              />
            </div>
            {formik.touched.eventStartTime && formik.errors.eventStartTime && (
              <div className="flex items-center mt-1 text-xs text-red-600">
                <AlertCircle className="mr-1 w-3 h-3" />
                {formik.errors.eventStartTime}
              </div>
            )}
          </div>

          {/* End Time */}
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-gray-700">
              End Time <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
              <input
                type="time"
                name="eventEndTime"
                value={formik.values.eventEndTime}
                onChange={(e) => handleFieldChange("eventEndTime", e.target.value)}
                onBlur={formik.handleBlur}
                className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                  formik.touched.eventEndTime && formik.errors.eventEndTime
                    ? "border-red-300 bg-red-50"
                    : "border-gray-200 bg-gray-50 focus:bg-white"
                }`}
              />
            </div>
            {formik.touched.eventEndTime && formik.errors.eventEndTime && (
              <div className="flex items-center mt-1 text-xs text-red-600">
                <AlertCircle className="mr-1 w-3 h-3" />
                {formik.errors.eventEndTime}
              </div>
            )}
          </div>
        </div>

        {/* Venue Name */}
        <div>
          <label className="block mb-1.5 text-xs font-semibold text-gray-700">
            Venue Name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
            <input
              type="text"
              name="venueName"
              placeholder="e.g., National Theatre, Accra"
              value={formik.values.venueName}
              onChange={(e) => handleFieldChange("venueName", e.target.value)}
              onBlur={formik.handleBlur}
              className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                formik.touched.venueName && formik.errors.venueName
                  ? "border-red-300 bg-red-50"
                  : "border-gray-200 bg-gray-50 focus:bg-white"
              }`}
            />
          </div>
          {formik.touched.venueName && formik.errors.venueName && (
            <div className="flex items-center mt-1 text-xs text-red-600">
              <AlertCircle className="mr-1 w-3 h-3" />
              {formik.errors.venueName}
            </div>
          )}
        </div>

        {/* Venue Street Address */}
        <div>
          <label className="block mb-1.5 text-xs font-semibold text-gray-700">
            Venue Street Address <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
            <input
              type="text"
              name="venueStreetAddress"
              placeholder="e.g., 28 Independence Avenue"
              value={formik.values.venueStreetAddress}
              onChange={(e) =>
                handleFieldChange("venueStreetAddress", e.target.value)
              }
              onBlur={formik.handleBlur}
              className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                formik.touched.venueStreetAddress &&
                formik.errors.venueStreetAddress
                  ? "border-red-300 bg-red-50"
                  : "border-gray-200 bg-gray-50 focus:bg-white"
              }`}
            />
          </div>
          {formik.touched.venueStreetAddress &&
            formik.errors.venueStreetAddress && (
              <div className="flex items-center mt-1 text-xs text-red-600">
                <AlertCircle className="mr-1 w-3 h-3" />
                {formik.errors.venueStreetAddress}
              </div>
            )}
        </div>

        {/* Country and City/State/Region */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Country */}
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-gray-700">
              Country <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
              <input
                type="text"
                name="country"
                placeholder="e.g., Ghana"
                value={formik.values.country}
                onChange={(e) => handleFieldChange("country", e.target.value)}
                onBlur={formik.handleBlur}
                className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                  formik.touched.country && formik.errors.country
                    ? "border-red-300 bg-red-50"
                    : "border-gray-200 bg-gray-50 focus:bg-white"
                }`}
              />
            </div>
            {formik.touched.country && formik.errors.country && (
              <div className="flex items-center mt-1 text-xs text-red-600">
                <AlertCircle className="mr-1 w-3 h-3" />
                {formik.errors.country}
              </div>
            )}
          </div>

          {/* City/State/Region */}
          <div>
            <label className="block mb-1.5 text-xs font-semibold text-gray-700">
              City/State/Region <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
              <input
                type="text"
                name="city"
                placeholder="e.g., Accra, Greater Accra"
                value={formik.values.city}
                onChange={(e) => handleFieldChange("city", e.target.value)}
                onBlur={formik.handleBlur}
                className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                  formik.touched.city && formik.errors.city
                    ? "border-red-300 bg-red-50"
                    : "border-gray-200 bg-gray-50 focus:bg-white"
                }`}
              />
            </div>
            {formik.touched.city && formik.errors.city && (
              <div className="flex items-center mt-1 text-xs text-red-600">
                <AlertCircle className="mr-1 w-3 h-3" />
                {formik.errors.city}
              </div>
            )}
          </div>
        </div>

        {/* Venue Google Location */}
        <div>
          <label className="block mb-1.5 text-xs font-semibold text-gray-700">
            Venue Google Location
            
          </label>
          <div className="relative">
            <Navigation className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
            <input
              type="url"
              name="venueGoogleLocation"
              placeholder="https://maps.google.com/..."
              value={formik.values.venueGoogleLocation}
              onChange={(e) =>
                handleFieldChange("venueGoogleLocation", e.target.value)
              }
              onBlur={formik.handleBlur}
              className={`w-full pl-10 pr-3 py-2.5 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                formik.touched.venueGoogleLocation &&
                formik.errors.venueGoogleLocation
                  ? "border-red-300 bg-red-50"
                  : "border-gray-200 bg-gray-50 focus:bg-white"
              }`}
            />
          </div>
          {formik.touched.venueGoogleLocation &&
            formik.errors.venueGoogleLocation && (
              <div className="flex items-center mt-1 text-xs text-red-600">
                <AlertCircle className="mr-1 w-3 h-3" />
                {formik.errors.venueGoogleLocation}
              </div>
            )}
          <p className="mt-1 text-xs text-gray-500">
            Share a Google Maps link to help attendees find the venue easily
          </p>
        </div>
      </div>
    </div>
  );
};

export default EventVenueInfoSection;

